import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ButtonModule } from 'primeng/button';
import { ComplianceAction } from 'src/app/core-module/models/Audits/compliance-action';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { UtilService } from 'src/app/core-module/services/util.service';
import { CustomerTermPipe, CustomerTextPipe } from 'src/app/shared-module/pipes/customer-term.pipe';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';

@Component({
  selector: 'app-event-action-add',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule, SelectModule, TextareaModule, ButtonModule, CustomerTermPipe, CustomerTextPipe],
  templateUrl: './event-action-add.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './event-action-add.component.scss'
})
export class EventActionAddComponent implements OnInit, OnChanges {
  private readonly customerContext = inject(CustomerContextService);

  @Input() eventId: number = -1;
  @Input() locationId: number = -1;
  @Input() users: BasicUser[] = [];
  @Input() saving: boolean = false;
  @Input() resetToken: number = 0;
  @Input() auditOptions: { AuditID: number; AuditName: string }[] = [];
  @Input() auditsLoading: boolean = false;

  @Output() saveAction = new EventEmitter<ComplianceAction>();
  @Output() cancel = new EventEmitter<void>();

  public newAction: ComplianceAction = new ComplianceAction();
  public currentUser: BasicUser = new BasicUser({ UserID: -1, DisplayName: 'Please Select...', Seq: 0, UserTypeID: 0 });
  public showDescriptionError: boolean = false;
  public showAssigneeError: boolean = false;
  public showAuditError: boolean = false;
  public minDate: Date = this.startOfToday();
  public actionVariations: { label: string; value: number; description: string }[] = [
    { label: 'Standard action', value: 1, description: 'Complete directly on the event.' },
    { label: this.customerContext.displayText('Requires audit completion'), value: 2, description: this.customerContext.displayText('Resolution requires completing a follow-up audit.') }
  ];
  public selectedVariation: number = 1;
  public selectedAuditId: number | null = null;

  constructor(private util: UtilService) { }

  ngOnInit(): void {
    console.log(this.users);
    this.resetState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventId'] || changes['locationId']) {
      this.newAction.EventID = this.eventId;
      this.newAction.LocationID = this.locationId;
    }
    if (changes['resetToken'] && !changes['resetToken'].firstChange) {
      this.resetState();
    }
    if (changes['users']) {
      this.setDefaultUser();
    }
  }

  onDateChange(event: Date) {
    this.newAction.ActionDeadline = this.util.removeTimezoneFromDate(event);
  }

  submit() {
    this.showDescriptionError = !this.newAction.ActionNote || this.newAction.ActionNote.length === 0;
    this.showAssigneeError = this.currentUser.UserID === -1;
    this.showAuditError = false;
    if (this.selectedVariation === 2 && (!this.selectedAuditId || this.selectedAuditId <= 0)) {
      this.showAuditError = true;
    }
    if (this.showDescriptionError || this.showAssigneeError){
      return;
    }
    if (this.showAuditError){
      return;
    }
    this.newAction.ActionOwner = this.currentUser;
    this.newAction.ActionOwnerTypeID = this.currentUser.UserTypeID;
    this.newAction.EventID = this.eventId;
    this.newAction.LocationID = this.locationId;
    this.newAction.ActionVariationID = this.selectedVariation;
    this.newAction.AuditID = this.selectedVariation === 2 ? (this.selectedAuditId ?? -1) : -1;
    this.newAction.ActionTypeID = 2;
    this.saveAction.emit(this.newAction);
  }

  private resetState() {
    this.newAction = new ComplianceAction({ EventID: this.eventId, LocationID: this.locationId });
    this.newAction.ActionDeadline = new Date();
    this.newAction.ActionTypeID = 2;
    this.newAction.ActionVariationID = 1;
    this.newAction.AuditID = -1;
    this.showDescriptionError = false;
    this.showAssigneeError = false;
    this.showAuditError = false;
    this.selectedVariation = 1;
    this.selectedAuditId = null;
    this.setDefaultUser();
  }

  private setDefaultUser() {
    const placeholder = this.users.find(u => u.UserID === -1);
    if (placeholder) {
      this.currentUser = placeholder;
      (this.currentUser as any).Disabled = true;
    }
  }

  onDescriptionChange(){
    if (this.newAction.ActionNote && this.newAction.ActionNote.length > 0){
      this.showDescriptionError = false;
    }
  }

  onAssigneeChange(selected: BasicUser){
    this.currentUser = selected;
    if (this.currentUser && this.currentUser.UserID !== -1){
      this.showAssigneeError = false;
    }
  }

  onVariationChange(value: number){
    this.selectedVariation = value;
    if (value !== 2){
      this.selectedAuditId = null;
      this.showAuditError = false;
    }
  }

  onAuditChange(value: number){
    this.selectedAuditId = value;
    if (value && value > 0){
      this.showAuditError = false;
    }
  }

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0,0,0,0);
    return d;
  }
}
